import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Stepper, Step, StepLabel, Chip, Typography, Paper, Alert, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import TaskGeneratorCommandBar from './TaskGeneratorCommandBar';
import TaskGeneratorPreview from './TaskGeneratorPreview';
import TicketList from './TicketList';
import { fetchTaskGeneratorLatest } from '../../store/strategistSlice';
import './StrategistAgent.scss';

const TASK_GENERATOR_STEPS = {
  TICKET_SELECTION: 0,
  TASK_PREVIEW: 1,
  TICKET_GENERATION: 2,
};

const TaskGeneratorStepper = ({
  projectId,
  resetTrigger,
  refreshTrigger,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { taskGeneratorLatest, loading } = useSelector((state) => state.strategist);

  const [expandedSteps, setExpandedSteps] = useState(new Set([0]));
  const [isReset, setIsReset] = useState(false);
  const [lastProjectId, setLastProjectId] = useState(null);
  const [lastRefreshTrigger, setLastRefreshTrigger] = useState(0);
  const commandBarRef = React.useRef();

  // Derive display data: null when reset, otherwise from Redux
  const taskGenerationData = isReset ? null : taskGeneratorLatest;

  const transformedTickets = useMemo(() => {
    const raw = taskGenerationData?.generated_tickets;
    if (!Array.isArray(raw) || raw.length === 0) return [];
    const map = {};
    raw.forEach((ticket) => {
      map[ticket.id] = {
        id: ticket.id,
        ticketNumber: ticket.ticket_number,
        title: ticket.title,
        type: ticket.task_type || 'Task',
        priority: ticket.priority || 'Medium',
        children: [],
      };
    });
    const roots = [];
    raw.forEach((ticket) => {
      if (ticket.parent_ticket_id && map[ticket.parent_ticket_id]) {
        map[ticket.parent_ticket_id].children.push(map[ticket.id]);
      } else {
        roots.push(map[ticket.id]);
      }
    });
    return roots;
  }, [taskGenerationData]);

  const hasGeneratedTickets = transformedTickets.length > 0;
  const hasGeneratedTasks = taskGenerationData?.agent_output?.tasks?.length > 0;
  const hasGenerationError = Boolean(
    taskGenerationData?.status === 'FAILED' ||
    taskGenerationData?.agent_output?.error ||
    taskGenerationData?.agent_output?.parseError
  );
  const isTaskGenerationPending = Boolean(
    taskGenerationData &&
    taskGenerationData.status === 'PENDING' &&
    !hasGeneratedTasks &&
    !hasGeneratedTickets &&
    !hasGenerationError
  );

  const clearTaskGenerationData = () => {
    setIsReset(true);
    setExpandedSteps(new Set([0]));
    if (commandBarRef.current) {
      commandBarRef.current.clearFields();
    }
  };

  const fetchTaskGenerationData = useCallback(async () => {
    if (!projectId) return;
    setIsReset(false);
    await dispatch(fetchTaskGeneratorLatest(projectId));
  }, [projectId, dispatch]);

  // Handle reset trigger
  useEffect(() => {
    if (resetTrigger > 0) {
      clearTaskGenerationData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetTrigger]);

  // Handle refresh trigger
  useEffect(() => {
    if (refreshTrigger > 0 && refreshTrigger !== lastRefreshTrigger && projectId) {
      setLastRefreshTrigger(refreshTrigger);
      fetchTaskGenerationData();
    }
  }, [refreshTrigger, projectId, lastRefreshTrigger, fetchTaskGenerationData]);

  // On project change: reset local state and fetch fresh data
  useEffect(() => {
    if (projectId && projectId !== lastProjectId) {
      setIsReset(false);
      setLastProjectId(projectId);
      setExpandedSteps(new Set([0]));
      dispatch(fetchTaskGeneratorLatest(projectId));
    }
  }, [projectId, lastProjectId, dispatch]);

  // Auto-expand step 2 while generating so the loader is visible
  useEffect(() => {
    if (loading.generateTask) {
      setExpandedSteps(new Set([1]));
    }
  }, [loading.generateTask]);

  // Auto-expand based on data
  useEffect(() => {
    if (isReset || !taskGeneratorLatest) return;
    const latestHasTasks = taskGeneratorLatest.agent_output?.tasks?.length > 0;
    const latestHasTickets = Array.isArray(taskGeneratorLatest.generated_tickets) && taskGeneratorLatest.generated_tickets.length > 0;
    const latestHasError = taskGeneratorLatest.status === 'FAILED' || taskGeneratorLatest.agent_output?.error || taskGeneratorLatest.agent_output?.parseError;
    const latestIsPending = taskGeneratorLatest.status === 'PENDING' && !latestHasTasks && !latestHasTickets && !latestHasError;

    if (latestHasTickets) {
      setExpandedSteps(new Set([2]));
    } else if (latestHasTasks || latestIsPending || latestHasError) {
      setExpandedSteps(new Set([1]));
    } else {
      setExpandedSteps(new Set([0]));
    }
  }, [taskGeneratorLatest, isReset]);

  const steps = [
    { label: t('agent.taskGenerator.stepper.ticketSelection', 'Ticket Selection'), key: TASK_GENERATOR_STEPS.TICKET_SELECTION },
    { label: t('agent.taskGenerator.stepper.taskPreview', 'Task Preview'), key: TASK_GENERATOR_STEPS.TASK_PREVIEW },
    { label: t('agent.taskGenerator.stepper.ticketGeneration', 'Ticket Generation'), key: TASK_GENERATOR_STEPS.TICKET_GENERATION },
  ];

  const getActiveStep = () => {
    if (!taskGenerationData) return 0;
    if (hasGeneratedTickets) return 2;
    if (hasGeneratedTasks || isTaskGenerationPending || hasGenerationError) return 1;
    return 0;
  };

  const getStepStatus = (stepIndex) => {
    if (!taskGenerationData) return 'PENDING';

    if (stepIndex === 0) return (hasGeneratedTasks || hasGeneratedTickets || isTaskGenerationPending || hasGenerationError) ? 'COMPLETED' : 'PENDING';
    if (stepIndex === 1) {
      if (hasGeneratedTickets) return 'COMPLETED';
      if (hasGeneratedTasks || isTaskGenerationPending) return 'IN_PROGRESS';
      return 'PENDING';
    }
    if (stepIndex === 2) return hasGeneratedTickets ? 'COMPLETED' : 'PENDING';
    return 'PENDING';
  };

  const getStepStatusColor = (status) => {
    switch (status) {
      case 'COMPLETED':
        return '#16a34a';
      case 'IN_PROGRESS':
        return '#3b82f6';
      case 'PENDING':
      default:
        return '#6b7280';
    }
  };

  const handleStepClick = (stepIndex) => {
    setExpandedSteps((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(stepIndex)) {
        newSet.delete(stepIndex);
      } else {
        newSet.add(stepIndex);
      }
      return newSet;
    });
  };

  return (
    <Box className="breakdown-stepper-container">
      <Stepper activeStep={getActiveStep()} orientation="vertical" className="breakdown-stepper">
        {/* Step 1: Ticket Selection */}
        <Step completed={getActiveStep() > 0}>
          <StepLabel onClick={() => handleStepClick(0)} data-expanded={expandedSteps.has(0)}>
            <Box className="step-header">
              <Box className="step-title">
                {steps[0].label}
              </Box>
              <Chip
                label={getStepStatus(0)}
                size="small"
                className="step-status"
                sx={{ bgcolor: getStepStatusColor(getStepStatus(0)), color: 'white' }}
              />
            </Box>
          </StepLabel>
          {expandedSteps.has(0) && (
            <Box className="step-content">
              <TaskGeneratorCommandBar
                projectId={projectId}
                initialTicketId={taskGenerationData?.ticket_id}
                initialTicketNumber={taskGenerationData?.ticket_number}
                initialTicketTitle={taskGenerationData?.ticket_title}
                isGenerating={loading.fetchTaskGeneratorLatest || loading.generateTask || isTaskGenerationPending}
                onTaskGenerated={fetchTaskGenerationData}
                onClearRequested={commandBarRef}
              />
            </Box>
          )}
        </Step>

        {/* Step 2: Task Preview */}
        <Step completed={getActiveStep() > 1}>
          <StepLabel onClick={() => handleStepClick(1)} data-expanded={expandedSteps.has(1)}>
            <Box className="step-header">
              <Box className="step-title">
                {steps[1].label}
              </Box>
              <Chip
                label={getStepStatus(1)}
                size="small"
                className="step-status"
                sx={{ bgcolor: getStepStatusColor(getStepStatus(1)), color: 'white' }}
              />
            </Box>
          </StepLabel>
          {expandedSteps.has(1) && (
            <Box className="step-content">
              {hasGenerationError ? (
                <Paper elevation={2} className="breakdown-preview-paper">
                  <Alert severity="error" className="breakdown-preview-error-alert" sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box>
                      {t('agent.taskGenerator.agentIssue', 'The Agent ran into an issue while generating your tasks')}
                      <> : {taskGenerationData?.agent_output?.error || taskGenerationData?.agent_output?.parseError}</>
                      <br />
                      {t('agent.breakdown.pleaseTryAgain', 'Please try again')}
                    </Box>
                  </Alert>
                </Paper>
              ) : taskGenerationData?.agent_output?.tasks?.length > 0 ? (
                <TaskGeneratorPreview
                  tasks={taskGenerationData.agent_output}
                  projectId={projectId}
                  ticketsGenerated={!!taskGenerationData.generated_tickets}
                  agentLogId={taskGenerationData.agent_log_id}
                  breakdownHistoryId={taskGenerationData.breakdown_history_id}
                  parentTicketId={taskGenerationData.ticket_id}
                  onRefresh={fetchTaskGenerationData}
                />
              ) : loading.generateTask || loading.fetchTaskGeneratorLatest || isTaskGenerationPending ? (
                <Box className="ac-generating-container">
                  <Box sx={{ textAlign: 'center' }}>
                    <CircularProgress size={32} sx={{ mb: 2 }} />
                    <Typography variant="body1">
                      {t('agent.taskGenerator.generatingTasks', 'Generating tasks...')}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                      {t('agent.breakdown.refreshToSeeUpdates', 'Refresh to see updates')}
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Box className="step-placeholder">
                  <Typography variant="body2" color="text.secondary">
                    {t('agent.taskGenerator.stepper.selectTicket', 'Please select a ticket to generate tasks')}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Step>

        {/* Step 3: Ticket Generation */}
        <Step completed={getActiveStep() > 2}>
          <StepLabel onClick={() => handleStepClick(2)} data-expanded={expandedSteps.has(2)}>
            <Box className="step-header">
              <Box className="step-title">
                {steps[2].label}
              </Box>
              <Chip
                label={getStepStatus(2)}
                size="small"
                className="step-status"
                sx={{ bgcolor: getStepStatusColor(getStepStatus(2)), color: 'white' }}
              />
            </Box>
          </StepLabel>
          {expandedSteps.has(2) && (
            <Box className="step-content">
              {transformedTickets.length > 0 ? (
                <TicketList tickets={transformedTickets} hideHeader={true} />
              ) : (
                <Box className="step-placeholder">
                  <Typography variant="body2" color="text.secondary">
                    {t('agent.taskGenerator.stepper.completePreview', 'Please complete the task preview step to generate tickets')}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Step>
      </Stepper>
    </Box>
  );
};

export default TaskGeneratorStepper;
export { TASK_GENERATOR_STEPS };
